import { HummingAudioType, UploadForAcrResponseDTO } from '@/service/generatorUpload';
import { AudioDetailDto } from '@/service/interface';
import { AudioDto } from '@/service/type';
import { FilterGroup } from '@/service/type/Filter';
import { EditorOperatorEnum } from '@/state/editor-store';
import { EditorInstrumentEnum, EditorTypeEnum, FormValues, GeneratorTypeEnums } from '@/state/generator-store';
import { convertSecondsToMilliseconds } from '@/utils/time';
import { AudioEditor } from './module/AudioEditor';

export const modelList = [
  {
    displayName: '1.6+',
    version: '1.6sf',
    name: 'BETA',
    label: '1.6+',
  },
  {
    displayName: '2.0',
    version: '2.0',
    name: '',
    label: '2.0',
  },
];

export const editJen2ModelList = [
  {
    displayName: '2.0',
    version: '2.0',
    name: '',
    label: '2.0',
  },
];
export const editJen1ModelList = [
  {
    displayName: '1.6+',
    version: '1.6sf',
    name: 'BETA',
    label: '1.6+',
  },
];

export const qualityList = [
  {
    version: 'wav',
    name: 'WAV',
    label: 'WAV',
  },
  {
    version: 'mp3',
    name: 'MP3',
    label: 'MP3',
  },
];

export const assistList = [
  {
    version: 'ON',
    name: 'ON',
    label: 'ON',
  },
];

type PrepareGeneratorParamsOptions = {
  data: FormValues;
  type: GeneratorTypeEnums;
  editorType: 'instrument' | 'text';
  editorSelectedInstrument?: EditorInstrumentEnum;
  hummingAudioSelected?: null | HummingAudioType;
  generatorType: GeneratorTypeEnums;
  uploadFileData?: null | UploadForAcrResponseDTO;
  tracksCount?: number;
  qualityDefaultValue?: string;
};

export const prepareGeneratorParams = ({
  data,
  type,
  editorType,
  editorSelectedInstrument,
  hummingAudioSelected,
  generatorType,
  uploadFileData,
  tracksCount,
  qualityDefaultValue,
}: PrepareGeneratorParamsOptions) => {
  if (editorType === EditorTypeEnum.text) {
    const {
      prompt,
      model,
      quality,
      duration,
      assist,
      folderId,
      trackName,
      filterWeight,
      styleFilterId,
      styleFilterIsOpened,
      styleFilters,
    } = data;

    const param = {
      prompt,
      duration,
      fadeOutLength: 0,
      prompt_type: 'text',
      assist: assist === 'ON' ? true : false,
      format: quality ?? qualityDefaultValue,
      length: duration,
      model: model,
      folderId,
      trackName,
      tracksNum: tracksCount,
      ...(styleFilterIsOpened ? { filterWeight, styleFilterId, styleFilters } : {}),
    };

    return param;
  }
  if (type === GeneratorTypeEnums.R3IMAGINE || editorType === EditorTypeEnum.instrument) {
    const { prompt, model, quality, duration, assist, folderId, trackName } = data;
    const uploadFileId = generatorType === GeneratorTypeEnums.R3IMAGINE ? uploadFileData?.data?.id : hummingAudioSelected?.id;
    return {
      promptType: editorType,
      prompt_type: 'instrument',
      prompt: editorType === 'instrument' ? editorSelectedInstrument : prompt,
      negativePrompt: '',
      assist: assist === 'ON' ? true : false,
      steps: 100,
      strength: 1,
      duration,
      uploadFileId,
      reimagineType: editorType,
      fadeOutLength: 0,
      format: quality ?? qualityDefaultValue,
      model: `${model}` === '1.6' ? '1.6c' : model,
      folderId,
      tracksNum: tracksCount,
      trackName,
    };
  }
};

export const prepareEditorParams = ({
  data,
  editor,
  audioInfo,
  editorDuration,
  operator,
  tracksCount,
  qualityDefaultValue,
  currentFilterGroup,
  filterWeight,
}: {
  data: FormValues;
  editor: AudioEditor;
  audioInfo: AudioDto | AudioDetailDto;
  editorDuration: number;
  operator: EditorOperatorEnum;
  tracksCount?: number;
  qualityDefaultValue?: string;
  currentFilterGroup?: FilterGroup | null;
  filterWeight?: number | null;
}) => {
  const { prompt, model, quality, assist, folderId, trackName, styleFilterId, styleFilterIsOpened } = data;

  const { start, end } = editor?.regions?.getRegions()?.[0] ?? {
    start: 0,
    end: 0,
  };

  const { duration: audioDuration } = editor?.audio ?? { duration: 0 };

  const project = {
    folderId,
    format: quality ?? qualityDefaultValue,
    model: `${model}` === '1.6' ? '1.6c' : model,
    assist: assist === 'ON' ? true : false,
    tracksNum: tracksCount,
    ...(styleFilterIsOpened ? { filterWeight, styleFilterId } : {}),
    tracks: [
      {
        audioId: audioInfo?.id,
        prompt,
        trackName: trackName,
        clips: [
          {
            startTime: 0,
            endTime: convertSecondsToMilliseconds(audioDuration),
            effects: [
              {
                type: operator,
                params: getEffectParams({
                  mode: operator,
                  start,
                  end,
                  editorDuration,
                }),
              },
            ],
          },
        ],
      },
    ],
  };
  return project;
};

export type SimpleEditorInput = {
  audioId: string;
  mode: EditorOperatorEnum;
  trackName: string;
  userPrompt: string;
  start: number;
  end: number;
  editorDuration: number;
};
export const prepareSimpleEditorParams = (params: SimpleEditorInput) => {
  const { audioId, mode, trackName, userPrompt, start, end, editorDuration } = params;

  const project = {
    folderId: undefined,
    format: 'mp3',
    model: '1.6c',
    assist: true,
    tracks: [
      {
        audioId,
        prompt: userPrompt,
        trackName,
        clips: [
          {
            startTime: 0,
            endTime: convertSecondsToMilliseconds(60),
            effects: [
              {
                type: mode,
                params: getEffectParams({
                  mode,
                  start,
                  end,
                  editorDuration,
                }),
              },
            ],
          },
        ],
      },
    ],
  };
  return project;
};

type EffectParamsOptions = {
  mode?: EditorOperatorEnum;
  start: number;
  end: number;
  editorDuration?: number;
};

const getEffectParams = (options: EffectParamsOptions) => {
  const { mode, start, end, editorDuration } = options;
  if (mode === EditorOperatorEnum.INTRO || mode === EditorOperatorEnum.OUTRO) {
    return {
      keepTimes: convertSecondsToMilliseconds(editorDuration),
    };
  }
  if (mode === EditorOperatorEnum.REFILL) {
    return {
      duration: 60000,
      startTime: convertSecondsToMilliseconds(start),
      endTime: convertSecondsToMilliseconds(end),
    };
  }
  if (mode === EditorOperatorEnum.TRIM) {
    return {
      startTime: convertSecondsToMilliseconds(start),
      endTime: convertSecondsToMilliseconds(end),
    };
  }
  if (mode === EditorOperatorEnum.EXTEND) {
    return {
      duration: convertSecondsToMilliseconds(editorDuration),
      startTime: convertSecondsToMilliseconds(start),
      endTime: convertSecondsToMilliseconds(end),
      keepTimes: convertSecondsToMilliseconds(5),
    };
  }
  if (mode === EditorOperatorEnum.FADE_OUT) {
    return {
      keepTimes: convertSecondsToMilliseconds(editorDuration),
    };
  }
};

export function getModelVersion(audio: AudioDetailDto) {
  if (
    audio.model_version === 'jenxl_genre_sf_v20241206_c20241218_c' ||
    audio.model_version === 'jen_16-base-v20241206-c20241218-genre_sf-c' ||
    audio.model_version === 'jen_16-base-v20241206-c20241218-17cd-c'
  ) {
    return '1.6sf';
  }
  if (audio.model_version?.slice(0, 11) === 'jen_17-base') {
    return '1.7';
  }
  if (audio.model_version?.slice(0, 6) === 'jen_20') {
    return '2.0';
  }
  return '';
}
